import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  Box,
  InputAdornment,
} from "@mui/material";
import {
  getPickupOrderDetail,
  updatePickupOrder,
  getProcurementsForPickupOrder,
} from "../../api/biomassProcurement";
import { useAlert } from "../../components/Alert";
import CustomAppBar from "../../components/AppBar";
import PickupOrderCard from "../../components/Biochar/PickupOrderCard";
import {
  handleMobileKeyDown,
  isNotNullOrEmptyArray,
} from "../../globals/utils";
import ProcurementCard from "../../components/Biochar/ProcurementCard";
import { getUserBasicDetailFromAuth } from "../../utils/common";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const ManagePickupOrder = () => {
  const { id } = useParams();
  const { app_user_id } = getUserBasicDetailFromAuth();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const [pickupOrderDetail, setPickupOrderDetail] = useState(null);
  const [orderUpdates, setOrderUpdates] = useState(null);
  const [procurements, setProcurements] = useState([]);
  const [remarks, setRemarks] = useState("");

  const fetchPickupOrderDetails = async () => {
    try {
      const response = await getPickupOrderDetail(id);
      setPickupOrderDetail(response);
      const {
        transporter_name,
        transporter_mobile,
        transporter_vehicle_number,
        transporter_driver_name,
        transporter_driver_mobile,
      } = response;
      setOrderUpdates({
        transporter_name: transporter_name,
        transporter_mobile: transporter_mobile,
        transporter_vehicle_number: transporter_vehicle_number,
        transporter_driver_name: transporter_driver_name,
        transporter_driver_mobile: transporter_driver_mobile,
      });
    } catch (error) {
      console.error("Error fetching pickup orders:", error);
      showAlert("error", t("Failed to fetch pickup orders"));
    }
  };

  const fetchProcurementsForPickupOrder = async () => {
    try {
      const response = await getProcurementsForPickupOrder(id);
      setProcurements(response);
    } catch (error) {
      console.error("Error fetching procurements for pickup order:", error);
      showAlert("error", t("Failed to fetch procurements for pickup order"));
    }
  };

  const handleUpdatePickupOrder = async () => {
    try {
      await updatePickupOrder(id, orderUpdates);
      showAlert("success", t("Pickup order updated successfully"));
      fetchPickupOrderDetails();
    } catch (error) {
      console.error("Error updating pickup:", error);
      showAlert("error", t("Failed to update pickup order"));
    }
  };

  const handleUpdateOrderDetails = (e) => {
    setOrderUpdates({ ...orderUpdates, [e.target.name]: e.target.value });
  };

  const handleCompleteOrder = async () => {
    try {
      await updatePickupOrder(id, {
        status: "PICKED",
        pickup_remarks: remarks || "",
        picked_by: app_user_id,
        pickup_date: dayjs().format("YYYY-MM-DD"),
      });
      showAlert("success", t("Order completed successfully"));
      navigate("/pickup-orders");
    } catch (error) {
      console.error("Error completing order:", error);
      showAlert("error", t("Failed to complete order"));
    }
  };

  const isCompleteEnabled =
    isNotNullOrEmptyArray(procurements) &&
    procurements.every((procurement) => procurement?.status === "PICKED") &&
    pickupOrderDetail?.status !== "PICKED";

  useEffect(() => {
    fetchPickupOrderDetails();
    fetchProcurementsForPickupOrder();
  }, []);

  return (
    <>
      <CustomAppBar />
      <Typography variant="h5" m={2}>
        {t("Manage Pickup Order")}
      </Typography>
      <Box m={2}>
        {pickupOrderDetail && (
          <>
            <PickupOrderCard
              pickupOrder={pickupOrderDetail}
              navigate={navigate}
              hideNavButton
            />
            {orderUpdates && (
              <Paper sx={{ padding: 2, mt: 2 }}>
                <Typography variant="h6" mb={2}>
                  {t("Transport Details")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label={t("Transporter Name")}
                      name="transporter_name"
                      fullWidth
                      value={orderUpdates.transporter_name}
                      onChange={handleUpdateOrderDetails}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={t("Transporter Mobile")}
                      name="transporter_mobile"
                      fullWidth
                      value={orderUpdates.transporter_mobile}
                      onChange={handleUpdateOrderDetails}
                      type="tel"
                      inputProps={{
                        maxLength: 10,
                        inputMode: "numeric",
                      }}
                      onKeyDown={handleMobileKeyDown}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">+91</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={t("Vehicle Number")}
                      name="transporter_vehicle_number"
                      fullWidth
                      value={orderUpdates.transporter_vehicle_number}
                      onChange={handleUpdateOrderDetails}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={t("Driver Name")}
                      name="transporter_driver_name"
                      fullWidth
                      value={orderUpdates.transporter_driver_name}
                      onChange={handleUpdateOrderDetails}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={t("Driver Mobile")}
                      name="transporter_driver_mobile"
                      fullWidth
                      value={orderUpdates.transporter_driver_mobile}
                      onChange={handleUpdateOrderDetails}
                      type="tel"
                      inputProps={{
                        maxLength: 10,
                        inputMode: "numeric",
                      }}
                      onKeyDown={handleMobileKeyDown}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">+91</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleUpdatePickupOrder}
                      fullWidth
                    >
                      {t("Update Order Details")}
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            )}
          </>
        )}
        {isNotNullOrEmptyArray(procurements) && (
          <>
            <Typography variant="h6" m={2}>
              {t("Procurements")}
            </Typography>
            {procurements.map((procurement) => (
              <ProcurementCard
                key={procurement.id}
                procurement={procurement}
                navigate={navigate}
              />
            ))}
          </>
        )}
        <Typography variant="h6" m={2}>
          {t("Complete Order")}
        </Typography>

        <TextField
          label={t("Remarks")}
          name="remarks"
          fullWidth
          onChange={(e) => setRemarks(e.target.value)}
          value={remarks}
          multiline
          rows={4}
          disabled={!isCompleteEnabled}
        />

        <Button
          variant="contained"
          color="secondary"
          sx={{ mt: 2 }}
          onClick={handleCompleteOrder}
          fullWidth
          disabled={!isCompleteEnabled}
        >
          {t("Complete Order")}
        </Button>
      </Box>
    </>
  );
};

export default ManagePickupOrder;
